import { useEffect, useState } from 'react';
import { fetchStats, fetchTrendStats } from '../services/api';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import './StatsPage.css';

const COLORS = ['#28a745', '#ffc107', '#dc3545'];

// Utility funkcije za formatiranje brojeva
const formatCurrency = (amount, currency = 'RSD') => {
  if (!amount) return '0';
  const formatted = new Intl.NumberFormat('sr-RS', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return `${formatted} ${currency}`;
};

const formatNumber = (num) => {
  if (!num) return '0';
  return new Intl.NumberFormat('sr-RS', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export default function StatsPage({ apiKey, organizationId }) {
  const [stats, setStats] = useState(null);
  const [trendStats, setTrendStats] = useState(null);

  useEffect(() => {
      if (!apiKey || !organizationId) return;

      fetchStats(apiKey, organizationId)
        .then(setStats)
        .catch(err => alert(err.message));

      fetchTrendStats(apiKey, organizationId)
        .then(data => {
          console.log('📊 Trend stats received:', data);  // ✅ DEBUG
          console.log('📊 voucher_count:', data?.voucher_count);
          console.log('📊 months:', data?.months);
          setTrendStats(data);
        })
        .catch(err => console.error('Greška pri učitavanju trend podataka:', err));
    }, [apiKey, organizationId]);

  if (!stats) return <p>⏳ Učitavanje statistike...</p>;

  // Pripremi podatke za sparkline grafikone
  let voucherTrendData = [];
  let amountTrendData = [];
  let remainingTrendData = [];

  if (trendStats && trendStats.voucher_count && trendStats.voucher_count.length > 0) {
    voucherTrendData = trendStats.voucher_count.map((count, index) => ({
      value: count,
      month: trendStats.months ? trendStats.months[index] : `M${index + 1}`
    }));

    amountTrendData = trendStats.total_amount.map((amount, index) => ({
      value: parseFloat(amount) || 0,
      month: trendStats.months ? trendStats.months[index] : `M${index + 1}`
    }));

    remainingTrendData = trendStats.remaining_amount.map((remaining, index) => ({
      value: parseFloat(remaining) || 0,
      month: trendStats.months ? trendStats.months[index] : `M${index + 1}`
    }));
  }

      // Računanje trenda
    const calculateTrend = (data) => {
      // Filtriraj nenula vrednosti
      const nonZeroValues = data.filter(d => d.value > 0);

      // Proveri da li ima dovoljno podataka
      if (nonZeroValues.length === 0) return null;  // ✅ Vrati null umesto 0

      // Ako ima samo jedna vrednost, prikaži kao pozitivan rast
      if (nonZeroValues.length === 1) return 'new';

      // Izračunaj prosek svih vrednosti
      const sum = nonZeroValues.reduce((acc, item) => acc + item.value, 0);
      const average = sum / nonZeroValues.length;

      // Uzmi trenutnu (poslednju) vrednost
      const current = nonZeroValues[nonZeroValues.length - 1].value;

      // Izračunaj trend u odnosu na prosek
      const trend = ((current - average) / average) * 100;

      return trend.toFixed(1);
    };

  const voucherTrend = calculateTrend(voucherTrendData);
  const amountTrend = calculateTrend(amountTrendData);
  const remainingTrend = calculateTrend(remainingTrendData);

  const pieData = [
    { name: 'Potpuno iskorišćeni', value: stats.broj_potpuno },
    { name: 'Delimično iskorišćeni', value: stats.broj_polovicno },
    {
      name: 'Neiskorišćeni',
      value: stats.ukupno_vaucera - stats.broj_potpuno - stats.broj_polovicno
    }
  ];

  const totalVouchers = pieData.reduce((sum, item) => sum + item.value, 0);

  const pieDataWithPercent = pieData.map(item => ({
    ...item,
    percent: totalVouchers ? Math.round((item.value / totalVouchers) * 100) : 0
  }));

  const barData = [
    { name: 'Ukupno', value: stats.ukupno_iznos, fill: '#0ea5e9' },
    {
      name: 'Iskorišćeno',
      value: stats.ukupno_iznos - stats.ukupno_preostalo,
      fill: '#10b981'
    },
    { name: 'Preostalo', value: stats.ukupno_preostalo, fill: '#f97316' }
  ];

  // Custom tooltip za bar chart sa formatiranjem
  const CustomBarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1f2937',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #374151'
        }}>
          <p style={{ color: '#f9fafb', margin: 0, fontSize: '13px' }}>
            {payload[0].payload.name}
          </p>
          <p style={{ color: '#60a5fa', margin: '4px 0 0 0', fontWeight: 'bold' }}>
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="stats-page">
      <h2>📊 Statistika vaučera</h2>

      {/* KPI kartice sa formatiranim brojevima */}
      <div className="stats-grid">
        <div className="stats-card">
          <div className="stats-card-header">
            <span>Ukupno vaučera</span>
          </div>
          <div className="stats-card-body">
            <strong>{formatNumber(stats.ukupno_vaucera)}</strong>
            {voucherTrend === 'new' ? (
              <div className="trend trend-up">🆕 Novo</div>
            ) : voucherTrend !== null && voucherTrend != 0 && (
              <div className={`trend ${parseFloat(voucherTrend) >= 0 ? 'trend-up' : 'trend-down'}`}>
                {parseFloat(voucherTrend) >= 0 ? '↗' : '↘'} {Math.abs(voucherTrend)}%
              </div>
            )}
          </div>
          <div className="sparkline-container">
            <ResponsiveContainer width="100%" height={40}>
              <LineChart data={voucherTrendData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={parseFloat(voucherTrend) >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span>Ukupan iznos</span>
          </div>
          <div className="stats-card-body">
            <strong>{formatCurrency(stats.ukupno_iznos)}</strong>
            {amountTrend === 'new' ? (
              <div className="trend trend-up">🆕 Novo</div>
            ) : amountTrend !== null && parseFloat(amountTrend) !== 0 && (
              <div className={`trend ${parseFloat(amountTrend) >= 0 ? 'trend-up' : 'trend-down'}`}>
                {parseFloat(amountTrend) >= 0 ? '↗' : '↘'} {Math.abs(parseFloat(amountTrend))}%
              </div>
            )}
          </div>
          <div className="sparkline-container">
            <ResponsiveContainer width="100%" height={40}>
              <LineChart data={amountTrendData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={parseFloat(amountTrend) >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span>Ukupno preostalo</span>
          </div>
          <div className="stats-card-body">
            <strong>{formatCurrency(stats.ukupno_preostalo)}</strong>
            {remainingTrend === 'new' ? (
              <div className="trend trend-up">🆕 Novo</div>
            ) : remainingTrend !== null && parseFloat(remainingTrend) !== 0 && (
              <div className={`trend ${parseFloat(remainingTrend) >= 0 ? 'trend-up' : 'trend-down'}`}>
                {parseFloat(remainingTrend) >= 0 ? '↗' : '↘'} {Math.abs(parseFloat(remainingTrend))}%
              </div>
            )}
          </div>
          <div className="sparkline-container">
            <ResponsiveContainer width="100%" height={40}>
              <LineChart data={remainingTrendData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={parseFloat(remainingTrend) >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Grafikoni */}
      <div className="charts-grid">
        <div className="chart-card">
          <h4>Stanje vaučera</h4>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={45}
                outerRadius={100}
                paddingAngle={4}
              >
                {pieData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="pie-legend">
            {pieDataWithPercent.map((item, index) => (
              <div key={index} className="legend-item">
                <span
                  className="legend-color"
                  style={{ backgroundColor: COLORS[index] }}
                />
                <span className="legend-label">
                  {item.name}
                </span>
                <strong>
                  {formatNumber(item.value)} ({item.percent}%)
                </strong>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-card">
          <h4>Iznosi (RSD)</h4>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData}>
              <XAxis dataKey="name" />
              <YAxis
                tickFormatter={(value) => formatNumber(value)}
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar
                dataKey="value"
                fill="#007bff"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
